import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { QuizWizard } from '../components/QuizWizard';
import { Badge } from '../components/ui/Badge';
import { Container } from '../components/ui/Container';
import { Section } from '../components/ui/Section';
import { SEO } from '../components/SEO';
import { User, Building2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function RiskAssessment() {
  const [searchParams] = useSearchParams();
  const assessmentType = searchParams.get('type') || 'personal';
  const {
    t
  } = useLanguage();
  const [config, setConfig] = useState(getAssessmentConfig(assessmentType, t));
  useEffect(() => {
    setConfig(getAssessmentConfig(assessmentType, t));
  }, [assessmentType, t]);
  // Custom breadcrumbs based on assessment type
  const breadcrumbs = [{
    label: t('risk.breadcrumb'),
    path: '/start'
  }, {
    label: assessmentType === 'personal' ? t('start.personal.badge') : t('start.org.badge')
  }];
  // SEO based on assessment type
  const seoTitle = assessmentType === 'personal' ? 'Personal Security Risk Assessment | CyberSecurityProtocol 101' : 'Organizational Cybersecurity Risk Assessment | CyberSecurityProtocol 101';
  const seoDescription = assessmentType === 'personal' ? 'Evaluate your personal cybersecurity risk based on accounts, devices, and daily digital habits. Receive a personalized security protocol and prioritized action checklist.' : "Assess your organization's cybersecurity posture across identity, devices, data protection, and incident readiness. Get a tailored protocol and 30/60/90-day improvement roadmap.";
  return <Layout breadcrumbs={breadcrumbs} backButtonLabel={t('risk.breadcrumb')} backButtonPath="/start" data-id="element-4813">
      <SEO title={seoTitle} description={seoDescription} keywords={assessmentType === 'personal' ? 'personal security assessment, cybersecurity risk, personal cyber risk, security evaluation' : 'organizational security assessment, cybersecurity posture, enterprise security, security maturity'} url={`https://cybersecurityprotocol.org/risk-assessment?type=${assessmentType}`} data-id="element-4814" />

      <Section spacing="xl" data-id="element-4815">
        <Container size="lg" data-id="element-4816">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in-up" data-id="element-4817">
            <div className="flex items-center justify-center mb-4" data-id="element-4818">
              <div className={`h-16 w-16 rounded-2xl ${config.iconBg} flex items-center justify-center ${config.iconColor} mr-4`} data-id="element-4819">
                {config.icon}
              </div>
              <Badge variant={config.badgeVariant as any} size="lg" data-id="element-4820">
                {config.badge}
              </Badge>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4821">
              {config.title}
            </h1>

            <p className="text-lg text-slate-400 max-w-3xl mx-auto mb-3" data-id="element-4822">
              {config.description}
            </p>

            <p className="text-sm text-slate-500 max-w-2xl mx-auto italic" data-id="element-4823">
              {t('risk.disclaimer')}
            </p>
          </div>

          {/* Quiz Wizard */}
          <div className="animate-fade-in-up animation-delay-200" data-id="element-4824">
            <QuizWizard data-id="element-4825" />
          </div>
        </Container>
      </Section>
    </Layout>;
}
function getAssessmentConfig(type: string, t: any) {
  const configs = {
    personal: {
      icon: <User className="h-8 w-8" data-id="element-4826" />,
      iconBg: 'bg-cyan-500/10',
      iconColor: 'text-cyan-400',
      badge: t('risk.personal.badge'),
      badgeVariant: 'primary',
      title: t('risk.personal.title'),
      description: t('risk.personal.desc')
    },
    organization: {
      icon: <Building2 className="h-8 w-8" data-id="element-4827" />,
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400',
      badge: t('risk.org.badge'),
      badgeVariant: 'success',
      title: t('risk.org.title'),
      description: t('risk.org.desc')
    }
  };
  return configs[type as keyof typeof configs] || configs.personal;
}